<template>
  <div>
    <b-col md="12">
      <div>
        <b-card>
          <div>
            <!-- Print Button and Selection Actions -->
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h2>{{ $t("common.invoices") }}</h2>
              <div class="d-flex gap-2">
                <b-button
                  variant="primary"
                  size="sm"
                  @click="show(EditForm, 'create')"
                >
                  <feather-icon
                    icon="PlusIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ $t("hospital.invoice-insurance-surgery") }}
                </b-button>
                <b-button
                  v-if="!allSelected"
                  variant="outline-primary"
                  size="sm"
                  @click="selectAll"
                >
                  <feather-icon
                    icon="CheckSquareIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ $t("common.select-all") }}
                </b-button>
                <b-button
                  v-if="canBulkUpdate"
                  variant="warning"
                  size="sm"
                  @click="showBulkUpdateModal"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="14"
                    class="mr-1"
                  />
                  الموافقة على الفواتير ({{ selectedInvoices.length }})
                </b-button>
                <b-button
                  v-if="canBulkPrint"
                  variant="success"
                  size="sm"
                  @click="bulkPrintSummary"
                >
                  <feather-icon
                    icon="PrinterIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ $t("common.print-list") }}
                </b-button>
                <b-button
                  v-if="canBulkPrint"
                  variant="info"
                  size="sm"
                  @click="bulkPrintDetailed"
                >
                  <feather-icon
                    icon="FileTextIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ $t("common.print-detailed") }}
                </b-button>
                <b-button
                  v-if="selectedInvoices.length > 0"
                  variant="outline-secondary"
                  size="sm"
                  @click="clearSelection"
                >
                  {{ $t("common.clear-selection") }}
                </b-button>
              </div>
            </div>

            <!-- Bulk Selection Summary -->
            <b-row
              v-if="selectedInvoices.length > 0"
              class="mb-3"
            >
              <b-col cols="12">
                <b-alert
                  show
                  variant="info"
                  class="mb-0"
                >
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{{ $t("common.selected") }}: {{ selectedInvoices.length }} {{ $t("common.invoices") }}</strong>
                    </div>
                    <div>
                      <strong>{{ $t("common.total") }}: <span class="text-success">{{ formatPrice(selectedInvoicesTotal) }}</span></strong>
                    </div>
                  </div>
                </b-alert>
              </b-col>
            </b-row>

            <!-- Data Table -->
            <b-table
              v-if="!loading"
              :items="invoices.data"
              responsive
              :fields="tableColumns"
              primary-key="id"
              show-empty
              empty-text="No matching results found"
              class="position-relative"
            >
              <!-- Checkbox for selection -->
              <template #cell(select)="data">
                <b-form-checkbox
                  v-model="selectedInvoices"
                  :value="data.item"
                  @change="onSelectionChange"
                />
              </template>

              <template #cell(invoice_no)="data">
                <span class="font-weight-bold text-primary">
                  #{{ data.item.invoice_no }}
                </span>
              </template>

              <template #cell(service_items)="data">
                <span class="font-weight-bold text-dark">
                  {{ getNestedProperty(data.item, "surgery.title", "N/A") }}
                </span>
              </template>

              <template #cell(discount)="data">
                <span class="text-nowrap font-weight-bold text-warning">
                  {{ formatPrice(data.item.discount || 0) }}
                </span>
              </template>

              <template #cell(amount)="data">
                <span class="text-nowrap font-weight-bolder text-success">
                  {{ formatPrice(data.item.amount) }}
                </span>
              </template>

              <template #cell(surgery_invoice)="data">
                <b-badge
                  v-if="data.item.invoice_patient_surgery"
                  variant="info"
                  class="text-nowrap"
                  style="font-size: 0.95rem; padding: 0.4rem 0.6rem"
                >
                  #{{
                    getNestedProperty(
                      data.item,
                      "invoice_patient_surgery.invoice_no",
                      "N/A"
                    )
                  }}
                </b-badge>
              </template>

              <template #cell(status)="data">
                <div>
                  <ChangeInvoiceStatusDropDown
                    name="InsuranceInvoice"
                    endpoint="invoices"
                    :data="data.item"
                    @onEmit="getInvoices"
                  />
                </div>
              </template>

              <!-- Column: Actions -->
              <template #cell(actions)="data">
                <div class="text-nowrap d-flex">
                  <b-button
                    v-if="data.item.status == 'approved'"
                    variant="outline-primary"
                    size="sm"
                    class="mx-1"
                    @click="showModal(data.item)"
                  >
                    {{ $t("reception.Print Invoice") }}
                  </b-button>
                  <b-button
                    v-if="
                      hasPermission('apply-invoice-discount') &&
                        data.item.status !== 'printed' &&
                        data.item.status !== 'approved'
                    "
                    variant="outline-warning"
                    size="sm"
                    class="mx-1"
                    @click="showDiscountModal(data.item)"
                  >
                    <feather-icon
                      icon="PercentIcon"
                      size="14"
                      class="mr-1"
                    />
                    {{ $t("common.apply-discount") }}
                  </b-button>

                  <b-button
                    v-if="data.item.invoice_patient_surgery"
                    variant="outline-success"
                    size="sm"
                    class="mx-1"
                    @click="showSurgeryInvoice(data.item)"
                  >
                    <feather-icon
                      icon="EyeIcon"
                      size="14"
                      class="mr-1"
                    />
                    {{ $t("common.show-surgery-invoice") }}
                  </b-button>
                  <b-button
                    v-else
                    variant="outline-info"
                    size="sm"
                    class="mx-1"
                    @click="openAddSurgeryInvoiceModal(data.item)"
                  >
                    <feather-icon
                      icon="PlusIcon"
                      size="14"
                      class="mr-1"
                    />
                    {{ $t("common.add-surgery-invoice") }}
                  </b-button>
                </div>
              </template>
            </b-table>
            <template v-if="loading">
              <div>
                <div class="d-flex justify-content-center mb-1">
                  <b-spinner label="Loading..." />
                </div>
              </div>
            </template>
            <div class="mx-2 mb-2">
              <b-row>
                <b-col
                  cols="12"
                  sm="6"
                  class="d-flex align-items-center justify-content-center justify-content-sm-start"
                >
                  <span
                    class="text-muted"
                  >{{ $t("common.showing") }} 1 {{ $t("common.to") }} 10
                  </span>
                </b-col>
                <!-- Pagination -->
                <b-col
                  cols="12"
                  sm="6"
                  class="d-flex align-items-center justify-content-center justify-content-sm-end"
                >
                  <b-pagination
                    v-model="page"
                    :total-rows="dataCount"
                    :per-page="take"
                    first-number
                    last-number
                    class="mb-0 mt-1 mt-sm-0"
                    prev-class="prev-item"
                    next-class="next-item"
                  >
                    <template #prev-text>
                      <feather-icon
                        icon="ChevronLeftIcon"
                        size="18"
                      />
                    </template>
                  </b-pagination>
                </b-col>
              </b-row>
            </div>
          </div>
        </b-card>
      </div>

      <!-- Bulk Update Status Modal -->
      <b-modal
        id="modal-bulk-update-status"
        :no-close-on-backdrop="true"
        title="الموافقة على الفواتير المعلقة"
        ok-only
        ok-title="الموافقة على الفواتير"
        @ok="bulkUpdateStatus"
      >
        <b-card-text>
          <h5>الموافقة على الفواتير المعلقة</h5>
          <p>
            {{
              $t("common.selected-invoices-count", {
                count: selectedInvoices.length,
              })
            }}
          </p>

          <p class="font-weight-bold">
            هل تريد الموافقة على جميع الفواتير المعلقة المحددة؟
          </p>

          <div class="mt-3">
            <h6>{{ $t("common.selected-invoices") }}:</h6>
            <ul class="list-unstyled">
              <li
                v-for="invoice in selectedInvoices"
                :key="invoice.id"
                class="mb-1"
              >
                <small class="text-muted">
                  #{{ getNestedProperty(invoice, "invoice_no") }} -
                  {{ formatPrice(getNestedProperty(invoice, "amount")) }}
                </small>
              </li>
            </ul>
          </div>
        </b-card-text>
      </b-modal>

      <!-- Modal to apply discount -->
      <b-modal
        id="modal-apply-discount"
        centered
        size="md"
        :title="$t('common.apply-discount')"
        @ok="applyDiscount"
      >
        <b-card-text>
          <b-form-group
            :label="$t('common.invoice-number')"
            label-for="discount-invoice-no"
          >
            <b-form-input
              id="discount-invoice-no"
              :value="'#' + (discountForm.invoice_no || '')"
              disabled
            />
          </b-form-group>

          <b-form-group
            :label="$t('common.current-amount')"
            label-for="discount-current-amount"
          >
            <b-form-input
              id="discount-current-amount"
              :value="formatPrice(discountForm.amount)"
              disabled
            />
          </b-form-group>

          <b-form-group
            :label="$t('common.discount-amount')"
            label-for="discount-amount"
            :invalid-feedback="discountError"
            :state="discountError ? false : null"
          >
            <b-form-input
              id="discount-amount"
              v-model="discountForm.discount"
              type="number"
              min="0"
              :placeholder="$t('common.enter-discount-amount')"
              :state="discountError ? false : null"
            />
          </b-form-group>

          <b-alert
            v-if="discountForm.discount > 0"
            variant="info"
            show
            class="mt-2"
          >
            <div class="d-flex justify-content-between">
              <span>{{ $t("common.amount-after-discount") }}:</span>
              <strong>{{ formatPrice(calculateAmountAfterDiscount()) }}</strong>
            </div>
          </b-alert>
        </b-card-text>
      </b-modal>

      <!-- Print Confirmation Modal -->
      <b-modal
        id="modal-confirm-print"
        :no-close-on-backdrop="true"
        :title="$t('surgery.print-invoice')"
        ok-only
        :ok-title="$t('common.print')"
        @ok="printInvoice"
      >
        <b-card-text>
          <h5>{{ $t("surgery.print-invoice") }}</h5>
          {{ $t("surgery.print-sure") }}
        </b-card-text>
      </b-modal>

      <!-- Hidden print button -->
      <b-button
        ref="surgeryInsuranceInvoiceRef"
        v-print="SurgeryInsuranceInvoice"
        variant="outline-primary"
        size="sm"
        class="hidden"
      >
        {{ $t("reception.Print Invoice") }}
      </b-button>

      <SurgeryInsuranceInvoice :data="invoice" />

      <InvoiceSummaryPrint
        id="BulkPrintSummarySurgery"
        :invoices="bulkPrintInvoicesData"
        :total-amount="bulkPrintTotal"
        invoice-type="surgery"
      />

      <!-- Shared Invoice List PDF Component -->
      <InvoicesListPdf
        v-if="listPrintItemsSurgery.length > 0"
        ref="surgeryInvoicesListPdf"
        :patient-name="listPrintPatientName"
        :print-date="new Date()"
        :items="listPrintItemsSurgery"
        :title="$t('hospital.invoice-insurance-surgery')"
        filename="surgery-invoices-list"
        @hasStartedGeneration="isGeneratingPdf = true"
        @hasGenerated="isGeneratingPdf = false"
      />

      <!-- Loading Overlay -->
      <div
        v-if="isGeneratingPdf"
        class="pdf-loading-overlay"
      >
        <div class="pdf-loading-content">
          <b-spinner
            variant="primary"
            label="Loading..."
          />
          <p class="mt-2">
            {{ $t("common.generating-pdf") || "جاري إنشاء PDF..." }}
          </p>
        </div>
      </div>

      <!-- Bulk Print Detailed Components -->
      <div id="BulkPrintSurgeryInvoices">
        <div
          v-for="inv in bulkPrintInvoicesData"
          :key="`bulk-surgery-${inv.id}`"
          class="print-page-break"
        >
          <SurgeryInsuranceInvoice :data="inv" />
        </div>
      </div>

      <!-- Hidden print button for bulk printing -->
      <!-- Hidden print buttons -->
      <b-button
        v-if="bulkPrintInvoicesData.length > 0"
        ref="BulkPrintSummaryRef"
        v-print="BulkPrintSummaryConfig"
        variant="outline-primary"
        size="sm"
        class="hidden"
      >
        {{ $t("common.print-summary") }}
      </b-button>

      <b-button
        v-if="bulkPrintInvoicesData.length > 0"
        ref="BulkPrintRef"
        v-print="BulkPrintConfig"
        variant="outline-primary"
        size="sm"
        class="hidden"
      >
        {{ $t("common.print-detailed") }}
      </b-button>

      <!-- Add Insurance Invoice Modal -->
      <b-modal
        id="modal-out-invoice-edit"
        :no-close-on-backdrop="true"
        ok-only
        centered
        size="lg"
        :title="$t('hospital.invoice-insurance-surgery')"
        @hide="onModalHide"
        @ok="submitItem()"
      >
        <b-card-text>
          <b-row>
            <b-col
              cols="12"
              md="12"
              class="mb-2 mt-1"
            >
              <div class="mt-2 mt-md-0 w-100 mx-1">
                <b-form-checkbox
                  id="checkbox-1"
                  v-model="editDocAndSur"
                  name="checkbox-1"
                  :value="true"
                  :unchecked-value="false"
                >
                  {{ $t("surgery.edit-doctor-and-surgery") }}
                </b-form-checkbox>
              </div>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <div class="mt-2 mt-md-0 w-100 mx-1">
                <b-form-group
                  :label="$t('surgery.surgery')"
                  label-for="department"
                >
                  <v-select
                    v-model="surgerySelected"
                    :disabled="editDocAndSur"
                    label="title"
                    :options="surgeryDataList"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :get-option-label="(option) => option.surgery.title"
                    :placeholder="$t('surgery.choose-surgery')"
                  />
                </b-form-group>
              </div>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <div class="mt-2 mt-md-0 w-100 mx-1">
                <b-form-group
                  :label="$t('surgery.Doctor')"
                  label-for="department"
                >
                  <v-select
                    v-model="doctorSelected"
                    label="title"
                    :disabled="editDocAndSur"
                    :options="doctorDataList"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :get-option-label="(option) => option.name"
                  />
                </b-form-group>
              </div>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <div class="mt-2 mt-md-0 w-100 mx-1">
                <b-form-group
                  :label="$t('common.amount')"
                  label-for="BP"
                >
                  <b-form-input
                    id="BP"
                    v-model="EditForm.amount"
                    placeholder="Enter Patient BP"
                  />
                </b-form-group>
              </div>
            </b-col>
          </b-row>
        </b-card-text>
      </b-modal>

      <!-- add surgery invoice modal -->
      <b-modal
        id="modal-add-surgery-invoice"
        :no-close-on-backdrop="true"
        :title="$t('hospital.create-surgery-invoice')"
        ok-only
        size="lg"
        :ok-title="$t('common.add')"
        @ok="submitSurgeryInvoice"
      >
        <b-card-text>
          <b-row>
            <b-col lg="4">
              <b-form-group
                :label="$t('surgery.room_amount')"
                label-for="room_amount"
                class="required"
              >
                <b-form-input
                  id="room_amount"
                  v-model="surgeryInvoiceForm.room_amount"
                  type="number"
                  :placeholder="$t('surgery.room_amount')"
                />
              </b-form-group>
            </b-col>

            <b-col lg="4">
              <b-form-group
                :label="$t('surgery.admin_services_amount')"
                label-for="admin_services_amount"
                class="required"
              >
                <b-form-input
                  id="admin_services_amount"
                  v-model="surgeryInvoiceForm.admin_services_amount"
                  type="number"
                  :placeholder="$t('surgery.admin_services_amount')"
                />
              </b-form-group>
            </b-col>

            <b-col lg="4">
              <b-form-group
                :label="$t('surgery.general_services_amount')"
                label-for="general_services_amount"
                class="required"
              >
                <b-form-input
                  id="general_services_amount"
                  v-model="surgeryInvoiceForm.general_services_amount"
                  type="number"
                  :placeholder="$t('surgery.general_services_amount')"
                />
              </b-form-group>
            </b-col>

            <b-col lg="4">
              <b-form-group
                :label="$t('surgery.nursing_amount')"
                label-for="nursing_amount"
                class="required"
              >
                <b-form-input
                  id="nursing_amount"
                  v-model="surgeryInvoiceForm.nursing_amount"
                  type="number"
                  :placeholder="$t('surgery.nursing_amount')"
                />
              </b-form-group>
            </b-col>

            <b-col lg="4">
              <b-form-group
                :label="$t('surgery.surgery_room_amount')"
                label-for="surgery_room_amount"
                class="required"
              >
                <b-form-input
                  id="surgery_room_amount"
                  v-model="surgeryInvoiceForm.surgery_room_amount"
                  type="number"
                  :placeholder="$t('surgery.surgery_room_amount')"
                />
              </b-form-group>
            </b-col>

            <b-col lg="4">
              <b-form-group
                :label="$t('surgery.labour_room_amount')"
                label-for="labour_room_amount"
                class="required"
              >
                <b-form-input
                  id="labour_room_amount"
                  v-model="surgeryInvoiceForm.labour_room_amount"
                  type="number"
                  :placeholder="$t('surgery.labour_room_amount')"
                  class="required"
                />
              </b-form-group>
            </b-col>

            <b-col lg="4">
              <b-form-group
                :label="$t('surgery.medical_consumables_amount')"
                label-for="medical_consumables_amount"
                class="required"
              >
                <b-form-input
                  id="medical_consumables_amount"
                  v-model="surgeryInvoiceForm.medical_consumables_amount"
                  type="number"
                  :placeholder="$t('surgery.medical_consumables_amount')"
                />
              </b-form-group>
            </b-col>

            <b-col lg="4">
              <b-form-group
                :label="$t('surgery.pre_incubator_amount')"
                label-for="pre_incubator_amount"
                class="required"
              >
                <b-form-input
                  id="pre_incubator_amount"
                  v-model="surgeryInvoiceForm.pre_incubator_amount"
                  type="number"
                  :placeholder="$t('surgery.pre_incubator_amount')"
                />
              </b-form-group>
            </b-col>

            <b-col lg="6">
              <b-form-group
                :label="$t('surgery.pre_cpap_amount')"
                label-for="pre_cpap_amount"
                class="required"
              >
                <b-form-input
                  id="pre_cpap_amount"
                  v-model="surgeryInvoiceForm.pre_cpap_amount"
                  type="number"
                  :placeholder="$t('surgery.pre_cpap_amount')"
                />
              </b-form-group>
            </b-col>

            <b-col lg="6">
              <b-form-group
                :label="$t('surgery.scanner_device_amount')"
                label-for="scanner_device_amount"
                class="required"
              >
                <b-form-input
                  id="scanner_device_amount"
                  v-model="surgeryInvoiceForm.scanner_device_amount"
                  type="number"
                  :placeholder="$t('surgery.scanner_device_amount')"
                />
              </b-form-group>
            </b-col>
            <b-col lg="12">
              <hr style="border-top: 1px solid #000; margin: 20px 0">
            </b-col>
            <b-col lg="4">
              <b-form-group
                :label="$t('surgery.surgeon_amount')"
                label-for="surgeon_amount"
                class="required"
              >
                <b-form-input
                  id="surgeon_amount"
                  v-model="surgeryInvoiceForm.surgeon_amount"
                  type="number"
                  :placeholder="$t('surgery.surgeon_amount')"
                />
              </b-form-group>
            </b-col>

            <b-col lg="4">
              <b-form-group
                :label="$t('surgery.surgery_nurse_assist_amount')"
                label-for="surgery_nurse_assist_amount"
                class="required"
              >
                <b-form-input
                  id="surgery_nurse_assist_amount"
                  v-model="surgeryInvoiceForm.surgery_nurse_assist_amount"
                  type="number"
                  :placeholder="$t('surgery.surgery_nurse_assist_amount')"
                />
              </b-form-group>
            </b-col>

            <b-col lg="4">
              <b-form-group
                :label="$t('surgery.surgeon_assist_amount')"
                label-for="surgeon_assist_amount"
                class="required"
              >
                <b-form-input
                  id="surgeon_assist_amount"
                  v-model="surgeryInvoiceForm.surgeon_assist_amount"
                  type="number"
                  :placeholder="$t('surgery.surgeon_assist_amount')"
                />
              </b-form-group>
            </b-col>

            <b-col lg="4">
              <b-form-group
                :label="$t('surgery.anesthetist_amount')"
                label-for="anesthetist_amount"
                class="required"
              >
                <b-form-input
                  id="anesthetist_amount"
                  v-model="surgeryInvoiceForm.anesthetist_amount"
                  type="number"
                  :placeholder="$t('surgery.anesthetist_amount')"
                />
              </b-form-group>
            </b-col>

            <b-col lg="4">
              <b-form-group
                :label="$t('surgery.anaesthetist_assist_amount')"
                label-for="anaesthetist_assist_amount"
                class="required"
              >
                <b-form-input
                  id="anaesthetist_assist_amount"
                  v-model="surgeryInvoiceForm.anaesthetist_assist_amount"
                  type="number"
                  :placeholder="$t('surgery.anaesthetist_assist_amount')"
                />
              </b-form-group>
            </b-col>

            <b-col lg="4">
              <b-form-group
                :label="$t('surgery.pre_test_amount')"
                label-for="pre_test_amount"
                class="required"
              >
                <b-form-input
                  id="pre_test_amount"
                  v-model="surgeryInvoiceForm.pre_test_amount"
                  type="number"
                  :placeholder="$t('surgery.pre_test_amount')"
                />
              </b-form-group>
            </b-col>

            <b-col lg="4">
              <b-form-group
                :label="$t('surgery.tissue_transplant_amount')"
                label-for="tissue_transplant_amount"
                class="required"
              >
                <b-form-input
                  id="tissue_transplant_amount"
                  v-model="surgeryInvoiceForm.tissue_transplant_amount"
                  type="number"
                  :placeholder="$t('surgery.tissue_transplant_amount')"
                />
              </b-form-group>
            </b-col>

            <b-col lg="4">
              <b-form-group
                :label="$t('surgery.op_nurse_amount')"
                label-for="op_nurse_amount"
                class="required"
              >
                <b-form-input
                  id="op_nurse_amount"
                  v-model="surgeryInvoiceForm.op_nurse_amount"
                  type="number"
                  :placeholder="$t('surgery.op_nurse_amount')"
                  class="required"
                />
              </b-form-group>
            </b-col>

            <b-col lg="4">
              <b-form-group
                :label="$t('surgery.consultation_amount')"
                label-for="consultation_amount"
                class="required"
              >
                <b-form-input
                  id="consultation_amount"
                  v-model="surgeryInvoiceForm.consultation_amount"
                  type="number"
                  :placeholder="$t('surgery.consultation_amount')"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group
                :label="$t('accounting.pre_ventameter_amount')"
                label-for="pre_ventameter_amount"
                class="required"
              >
                <b-form-input
                  id="pre_ventameter_amount"
                  v-model="surgeryInvoiceForm.pre_ventameter_amount"
                  type="number"
                  :placeholder="$t('accounting.pre_ventameter_amount')"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group
                :label="$t('accounting.management_services_amount')"
                label-for="management_services_amount"
                class="required"
              >
                <b-form-input
                  id="management_services_amount"
                  v-model="surgeryInvoiceForm.management_services_amount"
                  type="number"
                  :placeholder="$t('accounting.management_services_amount')"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group
                :label="$t('accounting.delivery_room_amount')"
                label-for="delivery_room_amount"
                class="required"
              >
                <b-form-input
                  id="delivery_room_amount"
                  v-model="surgeryInvoiceForm.delivery_room_amount"
                  type="number"
                  :placeholder="$t('accounting.delivery_room_amount')"
                />
              </b-form-group>
            </b-col>
            <b-col lg="12">
              <b-form-group
                :label="$t('common.date')"
                label-for="date"
                class="required"
              >
                <b-form-input
                  id="date"
                  v-model="surgeryInvoiceForm.date"
                  :disabled="true"
                  type="datetime"
                  :placeholder="$t('common.date')"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-card-text>
      </b-modal>
      <!-- Modal: show single surgery invoice in expandable card -->
      <b-modal
        id="modal-show-surgery-invoice"
        :no-close-on-backdrop="true"
        :title="$t('hospital.patient-surgery-invoices')"
        ok-only
        size="xl"
        :dialog-class="'modal-xxl'"
        :ok-title="$t('common.ok')"
      >
        <b-card-text v-if="selectedSurgeryInvoice">
          <!-- Header badge (accordion-like) -->
          <b-badge
            variant="light"
            class="w-100 p-0"
            :class="{
              'badge-expanded': expandedInvoiceId === selectedSurgeryInvoice.id,
            }"
            style="display: block"
          >
            <div
              class="d-flex justify-content-between align-items-center p-2"
              role="tab"
            >
              <!-- Left: chevron + key info; click to expand -->
              <div
                class="flex-grow-1 d-flex align-items-center"
                style="cursor: pointer"
                @click="toggleCollapse(selectedSurgeryInvoice.id)"
              >
                <!-- Chevron -->
                <div class="mr-2">
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="30"
                    class="collapse-icon"
                    :class="{
                      'rotate-90':
                        expandedInvoiceId === selectedSurgeryInvoice.id,
                    }"
                  />
                </div>

                <!-- Summary chips -->
                <div class="d-flex align-items-center flex-wrap">
                  <b-badge
                    variant="primary"
                    class="mr-2"
                    style="font-size: 1rem; padding: 0.5rem 0.75rem"
                  >
                    #{{
                      selectedSurgeryInvoice.invoice_no ||
                        selectedSurgeryInvoice.id
                    }}
                  </b-badge>

                  <b-badge
                    variant="success"
                    class="mr-2"
                    style="font-size: 1rem; padding: 0.5rem 0.75rem"
                  >
                    {{
                      formatPrice(totalInvoiceAmount(selectedSurgeryInvoice))
                    }}
                  </b-badge>

                  <small class="text-muted ml-2">
                    <feather-icon
                      icon="InfoIcon"
                      size="14"
                      class="mr-1"
                    />
                    {{ $t("common.click-to-expand") }}
                  </small>
                </div>
              </div>

              <!-- Right: actions -->
              <div class="d-flex align-items-center">
                <div class="mr-2">
                  <ChangeInvoiceStatusDropDown
                    name="surgeryInvoice"
                    endpoint="invoicePatientSergury"
                    :data="selectedSurgeryInvoice"
                    @onEmit="getInvoices"
                  />
                </div>
                <b-button
                  variant="outline-warning"
                  size="sm"
                  @click="openEditDetailsModal(selectedSurgeryInvoice)"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ $t("common.edit") }}
                </b-button>
              </div>
            </div>
          </b-badge>

          <!-- Details collapse -->
          <b-collapse
            :id="`accordion-${selectedSurgeryInvoice.id}`"
            accordion="single-surgery-invoice"
            role="tabpanel"
            :visible="true"
            @show="expandedInvoiceId = selectedSurgeryInvoice.id"
            @hide="expandedInvoiceId = null"
          >
            <div class="collapse-content">
              <b-card-body>
                <!-- Hospital Services -->
                <b-row>
                  <b-col
                    lg="12"
                    class="mb-3"
                  >
                    <h5 class="text-primary">
                      {{ $t("surgery.hospital-services") }}
                    </h5>
                    <hr>
                  </b-col>

                  <b-col
                    v-for="f in hospitalFields"
                    :key="f.key"
                    lg="4"
                    md="6"
                    class="mb-2"
                  >
                    <strong>{{ $t(f.label) }}:</strong>
                    <span class="ml-2">{{
                      formatPrice(selectedSurgeryInvoice[f.key] || 0)
                    }}</span>
                  </b-col>

                  <!-- Doctor Services -->
                  <b-col
                    lg="12"
                    class="mt-3 mb-3"
                  >
                    <h5 class="text-primary">
                      {{ $t("surgery.doctor-services") }}
                    </h5>
                    <hr>
                  </b-col>

                  <b-col
                    v-for="f in doctorFields"
                    :key="f.key"
                    lg="4"
                    md="6"
                    class="mb-2"
                  >
                    <strong>{{ $t(f.label) }}:</strong>
                    <span class="ml-2">{{
                      formatPrice(selectedSurgeryInvoice[f.key] || 0)
                    }}</span>
                  </b-col>

                  <!-- Footer info -->
                  <b-col
                    lg="12"
                    class="mt-3"
                  >
                    <hr>
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <strong
                          class="text-muted"
                        >{{ $t("common.date") }}:</strong>
                        <span class="ml-2">{{
                          selectedSurgeryInvoice.date
                        }}</span>
                      </div>
                      <div>
                        <strong
                          class="text-primary"
                        >{{ $t("surgery.hospital_net_amount") }}:</strong>
                        <span class="ml-2 font-weight-bolder">
                          {{
                            formatPrice(
                              selectedSurgeryInvoice.hospital_net_amount || 0
                            )
                          }}
                        </span>
                      </div>
                    </div>
                  </b-col>
                </b-row>
              </b-card-body>
            </div>
          </b-collapse>
        </b-card-text>
      </b-modal>

      <b-modal
        id="modal-edit-surgery-invoice"
        :no-close-on-backdrop="true"
        :title="$t('hospital.update-surgery-invoice')"
        size="lg"
        :ok-title="$t('common.update')"
        @ok="updateSurgeryInvoice"
      >
        <b-card-text>
          <b-row>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.room_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.room_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.admin_services_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.admin_services_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.general_services_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.general_services_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.nursing_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.nursing_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.surgery_room_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.surgery_room_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.labour_room_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.labour_room_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.medical_consumables_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.medical_consumables_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.pre_incubator_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.pre_incubator_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.pre_cpap_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.pre_cpap_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.scanner_device_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.scanner_device_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('accounting.pre_ventameter_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.pre_ventameter_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group
                :label="$t('accounting.management_services_amount')"
              >
                <b-form-input
                  v-model="surgeryInvoiceEditForm.management_services_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('accounting.delivery_room_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.delivery_room_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="12">
              <hr style="border-top: 1px solid #000; margin: 20px 0">
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.surgeon_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.surgeon_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.surgery_nurse_assist_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.surgery_nurse_assist_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.surgeon_assist_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.surgeon_assist_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.anesthetist_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.anesthetist_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.anaesthetist_assist_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.anaesthetist_assist_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.pre_test_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.pre_test_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.tissue_transplant_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.tissue_transplant_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.op_nurse_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.op_nurse_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.consultation_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.consultation_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="12">
              <b-form-group :label="$t('common.date')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.date"
                  disabled
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-card-text>
      </b-modal>

      <b-modal
        id="modal-edit-surgery-invoice-details"
        :no-close-on-backdrop="true"
        :title="$t('hospital.update-surgery-invoice')"
        size="lg"
        :ok-title="$t('common.update')"
        @ok="updateEditedDetails"
      >
        <b-card-text>
          <b-row>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.room_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.room_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.admin_services_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.admin_services_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.general_services_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.general_services_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.nursing_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.nursing_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.surgery_room_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.surgery_room_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.labour_room_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.labour_room_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.medical_consumables_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.medical_consumables_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.pre_incubator_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.pre_incubator_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.pre_cpap_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.pre_cpap_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.scanner_device_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.scanner_device_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('accounting.pre_ventameter_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.pre_ventameter_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group
                :label="$t('accounting.management_services_amount')"
              >
                <b-form-input
                  v-model="surgeryInvoiceEditForm.management_services_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('accounting.delivery_room_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.delivery_room_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="12">
              <hr style="border-top: 1px solid #000; margin: 20px 0">
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.surgeon_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.surgeon_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.surgery_nurse_assist_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.surgery_nurse_assist_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.surgeon_assist_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.surgeon_assist_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.anesthetist_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.anesthetist_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.anaesthetist_assist_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.anaesthetist_assist_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.pre_test_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.pre_test_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.tissue_transplant_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.tissue_transplant_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.op_nurse_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.op_nurse_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('surgery.consultation_amount')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.consultation_amount"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="12">
              <b-form-group :label="$t('common.date')">
                <b-form-input
                  v-model="surgeryInvoiceEditForm.date"
                  disabled
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-card-text>
      </b-modal>
    </b-col>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BLink,
  BDropdown,
  BSpinner,
  BDropdownItem,
  BTooltip,
  BFormGroup,
  BFormSelect,
  BModal,
  BCardText,
  BFormCheckboxGroup,
  BFormCheckbox,
  BCardBody,
  BPagination,
  BCollapse,
  BCardHeader,
  BTable,
  BBadge,
  BAlert,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import VuePhoneNumberInput from 'vue-phone-number-input'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'
import { getNestedProperty } from '@/utils/nestedProperty.js'

import ActionsButtons from '@/components/common/ActionsButtons.vue'
import DeleteModal from '@/components/modals/DeleteModal.vue'

const AutoComplete = () => import('@/components/common/AutoComplete.vue')
const SurgeryInsuranceInvoice = () => import('@/components/printables/SurgeryInsuranceInvoice.vue')
const ChangeInvoiceStatusDropDown = () => import('@/components/invoices/ChangeInvoiceStatusDropDown.vue')
const InvoiceSummaryPrint = () => import('@/components/printables/InvoiceSummaryPrint.vue')
const InvoicesListPdf = () => import('@/components/printables/InvoicesListPdf.vue')
export default {
  components: {
    BCard,
    BRow,
    BBadge,
    BCol,
    BFormInput,
    BButton,
    BLink,
    BDropdown,
    BDropdownItem,
    BCollapse,
    BCardHeader,
    BTooltip,
    vSelect,
    ToastificationContent,
    VuePhoneNumberInput,
    DeleteModal,
    ActionsButtons,
    BFormGroup,
    BFormSelect,
    BSpinner,
    BModal,
    BCardText,
    BFormCheckbox,
    BFormCheckboxGroup,
    BCardBody,
    BPagination,
    BTable,
    BAlert,
    ChangeInvoiceStatusDropDown,
    SurgeryInsuranceInvoice,
    InvoiceSummaryPrint,
    InvoicesListPdf,
  },
  data() {
    return {
      tableColumns: [
        {
          key: 'select',
          label: '',
          thStyle: { width: '50px' },
        },
        {
          key: 'invoice_no',
          sortable: true,
          label: this.$t('hospital.invoice-no'),
        },
        {
          key: 'service_items',
          label: this.$t('reception.service'),
          sortable: false,
        },
        {
          key: 'discount',
          label: this.$t('common.discount'),
          sortable: true,
        },
        {
          key: 'amount',
          label: this.$t('employee.amount'),
          sortable: true,
        },
        {
          key: 'surgery_invoice',
          sortable: false,
          label: this.$t('hospital.surgery-invoice'),
        },
        {
          key: 'created_at',
          sortable: true,
          label: this.$t('common.created-at'),
        },
        {
          key: 'status',
          sortable: true,
          label: 'Status',
        },
        { key: 'Actions', label: this.$t('common.actions') },
      ],
      invoiceLatestStatus: '',
      InvoiceId: '',
      invoice: {},
      selectedInvoices: [],
      selectedInvoiceItems: [],
      selectedInvoiceNo: '',
      discountForm: {
        id: null,
        invoice_no: '',
        amount: 0,
        discount: 0,
      },
      discountError: '',
      bulkUpdateForm: {
        status: null,
      },
      statusOptions: [],
      SurgeryInsuranceInvoice: {
        id: 'SurgeryInsuranceInvoice',
      },
      currentPage: 1,
      takeOptions: [10, 25, 50, 100],
      searchQuery: '',
      sortBy: '-id',
      isSortDirDesc: true,
      page: 1,
      dataCount: 0,
      loading: false,
      take: 10,
      selectedItem: null,
      params: {
        form: {},
        path: '',
      },
      EditForm: {
        patient_card_id: '',
        doctor_id: '',
        amount: 0,
        _method: 'PUT',
      },
      createMode: false,
      errorForm: {
        patient_card_id: '',
        service_id: '',
        amount: 0,
      },
      invoicesData: [],
      surgeryDataList: [],
      surgerySelected: null,
      editDocAndSur: true,
      disabled: false,
      doctorSelected: null,
      doctorDataList: [],
      selectedDepositInvoice: null,
      selectedSurgeryInvoice: null,
      surgeryInvoiceForm: {
        room_amount: '',
        admin_services_amount: '',
        general_services_amount: '',
        nursing_amount: '',
        surgery_nurse_assist_amount: '',
        surgery_room_amount: '',
        labour_room_amount: '',
        medical_consumables_amount: '',
        pre_incubator_amount: '',
        pre_cpap_amount: '',
        scanner_device_amount: '',
        surgeon_amount: '',
        surgeon_assist_amount: '',
        anesthetist_amount: '',
        anaesthetist_assist_amount: '',
        pre_test_amount: '',
        tissue_transplant_amount: '',
        op_nurse_amount: '',
        consultation_amount: '',
        pre_ventameter_amount: '',
        management_services_amount: '',
        delivery_room_amount: '',
        date: new Date().toLocaleString('en-US', {
          hour: 'numeric',
          minute: 'numeric',
          hour12: true,
          timeZone: 'Asia/Baghdad',
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
        }),
      },
      surgeryInvoiceEditForm: {},
      newSurgeryInvoicePatientCardSurgeryId: null,
      invoices: { data: [] },
      invoiceDetailsTableColumns: [
        { key: 'room_amount', label: this.$t('surgery.room_amount') },
        {
          key: 'admin_services_amount',
          label: this.$t('surgery.admin_services_amount'),
        },
        {
          key: 'general_services_amount',
          label: this.$t('surgery.general_services_amount'),
        },
        { key: 'nursing_amount', label: this.$t('surgery.nursing_amount') },
        {
          key: 'surgery_nurse_assist_amount',
          label: this.$t('surgery.surgery_nurse_assist_amount'),
        },
        {
          key: 'surgery_room_amount',
          label: this.$t('surgery.surgery_room_amount'),
        },
        {
          key: 'labour_room_amount',
          label: this.$t('surgery.labour_room_amount'),
        },
        {
          key: 'medical_consumables_amount',
          label: this.$t('surgery.medical_consumables_amount'),
        },
        {
          key: 'pre_incubator_amount',
          label: this.$t('surgery.pre_incubator_amount'),
        },
        { key: 'pre_cpap_amount', label: this.$t('surgery.pre_cpap_amount') },
        {
          key: 'scanner_device_amount',
          label: this.$t('surgery.scanner_device_amount'),
        },
        { key: 'surgeon_amount', label: this.$t('surgery.surgeon_amount') },
        {
          key: 'surgeon_assist_amount',
          label: this.$t('surgery.surgeon_assist_amount'),
        },
        {
          key: 'anesthetist_amount',
          label: this.$t('surgery.anesthetist_amount'),
        },
        {
          key: 'anaesthetist_assist_amount',
          label: this.$t('surgery.anaesthetist_assist_amount'),
        },
        { key: 'pre_test_amount', label: this.$t('surgery.pre_test_amount') },
        {
          key: 'tissue_transplant_amount',
          label: this.$t('surgery.tissue_transplant_amount'),
        },
        { key: 'op_nurse_amount', label: this.$t('surgery.op_nurse_amount') },
        {
          key: 'consultation_amount',
          label: this.$t('surgery.consultation_amount'),
        },
        {
          key: 'pre_ventameter_amount',
          label: this.$t('accounting.pre_ventameter_amount'),
        },
        {
          key: 'management_services_amount',
          label: this.$t('accounting.management_services_amount'),
        },
        {
          key: 'delivery_room_amount',
          label: this.$t('accounting.delivery_room_amount'),
        },
        { key: 'date', label: this.$t('common.date') },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: this.$t('common.actions') },
      ],
      surgeryInvoiceEditForm: {},
      expandedInvoiceId: null,
      hospitalFields: [
        { key: 'room_amount', label: 'surgery.room_amount' },
        {
          key: 'admin_services_amount',
          label: 'surgery.admin_services_amount',
        },
        {
          key: 'general_services_amount',
          label: 'surgery.general_services_amount',
        },
        { key: 'nursing_amount', label: 'surgery.nursing_amount' },
        { key: 'surgery_room_amount', label: 'surgery.surgery_room_amount' },
        { key: 'labour_room_amount', label: 'surgery.labour_room_amount' },
        {
          key: 'medical_consumables_amount',
          label: 'surgery.medical_consumables_amount',
        },
        { key: 'pre_incubator_amount', label: 'surgery.pre_incubator_amount' },
        { key: 'pre_cpap_amount', label: 'surgery.pre_cpap_amount' },
        {
          key: 'scanner_device_amount',
          label: 'surgery.scanner_device_amount',
        },
        {
          key: 'delivery_room_amount',
          label: 'accounting.delivery_room_amount',
        },
        {
          key: 'management_services_amount',
          label: 'accounting.management_services_amount',
        },
        {
          key: 'pre_ventameter_amount',
          label: 'accounting.pre_ventameter_amount',
        },
      ],
      doctorFields: [
        { key: 'surgeon_amount', label: 'surgery.surgeon_amount' },
        {
          key: 'surgery_nurse_assist_amount',
          label: 'surgery.surgery_nurse_assist_amount',
        },
        {
          key: 'surgeon_assist_amount',
          label: 'surgery.surgeon_assist_amount',
        },
        { key: 'anesthetist_amount', label: 'surgery.anesthetist_amount' },
        {
          key: 'anaesthetist_assist_amount',
          label: 'surgery.anaesthetist_assist_amount',
        },
        { key: 'pre_test_amount', label: 'surgery.pre_test_amount' },
        {
          key: 'tissue_transplant_amount',
          label: 'surgery.tissue_transplant_amount',
        },
        { key: 'op_nurse_amount', label: 'surgery.op_nurse_amount' },
        { key: 'consultation_amount', label: 'surgery.consultation_amount' },
      ],

      bulkPrintInvoicesData: [],
      isGeneratingPdf: false,
      BulkPrintConfig: {
        id: 'BulkPrintSurgeryInvoices',
      },
      BulkPrintSummaryConfig: {
        id: 'BulkPrintSummarySurgery',
      },
    }
  },

  computed: {
    allSelected: {
      get() {
        const invoicesData = getNestedProperty(this.invoices, 'data', [])
        return (
          invoicesData.length > 0
          && this.selectedInvoices.length === invoicesData.length
        )
      },
      set(value) {
        // Handled by toggleSelectAll
      },
    },

    hasPermission() {
      return permission => this.$store.state.app.permissions
        && Array.isArray(this.$store.state.app.permissions)
        && this.$store.state.app.permissions.includes(permission)
    },

    canBulkUpdate() {
      if (this.selectedInvoices.length === 0) return false

      // Allow bulk status change only for pending invoices
      return this.selectedInvoices.every(invoice => {
        const status = getNestedProperty(invoice, 'status', '').toLowerCase()
        return status === 'pending'
      })
    },

    canBulkPrint() {
      if (this.selectedInvoices.length === 0) return false
      return this.selectedInvoices.every(invoice => {
        const status = getNestedProperty(invoice, 'status', '').toLowerCase()
        return status === 'approved'
      })
    },

    selectedInvoicesTotal() {
      return this.selectedInvoices.reduce((total, invoice) => {
        const amount = getNestedProperty(invoice, 'amount', 0)
        return total + (parseFloat(amount) || 0)
      }, 0)
    },

    bulkPrintTotal() {
      return this.bulkPrintInvoicesData.reduce((total, invoice) => total + (Number(invoice.amount) || 0), 0)
    },

    listPrintItemsSurgery() {
      if (!this.bulkPrintInvoicesData || this.bulkPrintInvoicesData.length === 0) {
        return []
      }
      console.log('bulkPrintInvoicesData', JSON.stringify(this.bulkPrintInvoicesData))
      return this.bulkPrintInvoicesData.map(invoice => ({
        invoice_no: invoice.invoice_no,
        patient_name: getNestedProperty(invoice, 'patient_card.patient.name',
          getNestedProperty(invoice, 'patientCard.patient.name', 'N/A')),
        name: getNestedProperty(invoice, 'surgery.title', 'Surgery Invoice'),
        doctor_name: getNestedProperty(
          invoice,
          'doctor.name',
          getNestedProperty(
            invoice,
            'invoice_patient_surgery.patient_card_surgery.doctors_prices.items.0.doctor.name',
            getNestedProperty(
              invoice,
              'invoicePatientSurgery.patientCardSurgery.doctorsPrices.items.0.doctor.name',
              '-',
            ),
          ),
        ),
        amount: invoice.amount || 0,
        discount: invoice.discount || 0,
        date: invoice.date || invoice.created_at,
        created_at: invoice.created_at,
        id: invoice.id,
      }))
    },

    listPrintPatientName() {
      if (this.bulkPrintInvoicesData && this.bulkPrintInvoicesData.length > 0) {
        const firstInvoice = this.bulkPrintInvoicesData[0]
        return getNestedProperty(firstInvoice, 'patient_card.patient.name',
          getNestedProperty(firstInvoice, 'patientCard.patient.name', 'N/A'))
      }
      return 'N/A'
    },
  },

  watch: {
    page() {
      this.getInvoices()
    },
    take() {
      this.getInvoices()
    },
    editDocAndSur(newVal) {
      console.log('editDocAndSur', newVal)
      this.disabled = newVal !== 'accepted'
    },
    surgerySelected(newVal) {
      console.log('surgerySelected', newVal)
      if (newVal === null || newVal === undefined) {
        this.doctorDataList = []
        this.doctorSelected = null
      } else {
        // Extract doctors from the selected surgery
        const doctors = getNestedProperty(newVal, 'doctors', [])
        this.doctorDataList = doctors

        // Auto-select first doctor if not in edit mode and doctors exist
        if (!this.editDocAndSur && doctors.length > 0) {
          this.doctorSelected = doctors[0]
        } else if (this.editDocAndSur && doctors.length > 0) {
          // In edit mode, still update the list but don't auto-select
          this.doctorSelected = null
        }

        console.log('Updated doctorDataList:', this.doctorDataList)
      }
    },
  },
  mounted() {
    this.disabled = 'not_accepted'
    if (this.$route.params.patientCardId) {
      this.getInvoices()
      this.getSurgerys()
    }
  },

  methods: {
    async getInvoices() {
      const { patientId } = this.$route.params
      if (!patientId) return
      this.loading = true
      try {
        const { data } = await this.$http.get('/invoices', {
          params: {
            'filter[patientCard.patient_id]': patientId,
            'filter[type]': 'invoice_insurance_surgery',
            include: 'surgery,invoicePatientSurgery,patientCard',
            take: this.take,
            page: this.page,
            sort: this.sortBy,
          },
        })
        this.invoices = data
        this.dataCount = data?.meta?.total || 0
      } catch (error) {
        console.error('Error fetching invoices:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'Error',
            variant: 'danger',
            text: 'Failed to load invoices.',
          },
        })
      } finally {
        this.loading = false
      }
    },
    getNestedProperty,

    // Format price with currency
    formatPrice(price) {
      return price ? `${Number(price).toLocaleString()} IQD` : '0 IQD'
    },
    toggleCollapse(id) {
      this.$root.$emit('bv::toggle::collapse', `accordion-${id}`)
    },
    totalInvoiceAmount(inv) {
      // Sum all numeric fields we show (same as in the lists), fallback to 0
      const keys = [
        ...this.hospitalFields.map(f => f.key),
        ...this.doctorFields.map(f => f.key),
      ]
      return keys.reduce((sum, k) => sum + (Number(inv[k]) || 0), 0)
    },
    // Show all service items in modal
    showAllServiceItems(invoice) {
      this.selectedInvoiceItems = getNestedProperty(
        invoice,
        'invoice_items',
        [],
      )
      this.selectedInvoiceNo = invoice.invoice_no || ''
      this.$bvModal.show('modal-all-service-items')
    },

    // Calculate total for modal
    calculateModalTotal() {
      return this.selectedInvoiceItems.reduce((sum, item) => {
        const amount = getNestedProperty(item, 'net_amount', 0)
        return sum + (parseFloat(amount) || 0)
      }, 0)
    },

    // Show discount modal
    showDiscountModal(invoice) {
      this.discountForm = {
        id: invoice.id,
        invoice_no: invoice.invoice_no || '',
        amount: parseFloat(invoice.amount) || 0,
        discount: parseFloat(invoice.discount) || 0,
      }
      this.discountError = ''
      this.$bvModal.show('modal-apply-discount')
    },

    // Calculate amount after discount
    calculateAmountAfterDiscount() {
      const amount = parseFloat(this.discountForm.amount) || 0
      const discount = parseFloat(this.discountForm.discount) || 0
      return Math.max(0, amount - discount)
    },

    // Apply discount to invoice
    async applyDiscount(bvModalEvt) {
      bvModalEvt.preventDefault()

      // Validate discount
      const discount = parseFloat(this.discountForm.discount)
      const amount = parseFloat(this.discountForm.amount)

      if (isNaN(discount) || discount < 0) {
        this.discountError = this.$t('common.invalid-discount')
        return
      }

      if (discount > amount) {
        this.discountError = this.$t('common.discount-exceeds-amount')
        return
      }

      try {
        this.loading = true

        const payload = {
          discount,
        }

        await this.$http.put(`/invoices/${this.discountForm.id}`, payload)

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('common.discount-applied-successfully'),
          },
        })

        this.$bvModal.hide('modal-apply-discount')

        // Refresh data
        this.getInvoices()
      } catch (error) {
        console.error('Error applying discount:', error)

        // Handle validation errors (400)
        if (error.response && error.response.status === 400) {
          const errors = error.response.data

          // Check if discount field has errors
          if (errors.discount && Array.isArray(errors.discount)) {
            this.discountError = errors.discount[0]
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('common.error'),
                variant: 'danger',
                text: errors.discount[0],
              },
            })
          } else {
            // Generic validation error
            const firstError = Object.values(errors)[0]
            const errorMessage = Array.isArray(firstError)
              ? firstError[0]
              : firstError
            this.discountError = errorMessage
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('common.error'),
                variant: 'danger',
                text: errorMessage,
              },
            })
          }
        } else {
          // Generic error
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('common.error'),
              variant: 'danger',
              text: this.$t('common.discount-apply-error'),
            },
          })
        }
      } finally {
        this.loading = false
      }
    },

    async showModal(status) {
      this.selectedItem = status
      console.log('Selected invoice item:', this.selectedItem)
      this.$bvModal.show('modal-confirm-print')
    },

    async printInvoice() {
      try {
        this.loading = true

        const url = `/invoices?filter[id]=${this.selectedItem.id}&include=service,surgery,patientCard,doctor,invoicePatientSurgery`
        await this.getList(url)

        if (
          this.all_data
          && this.all_data.data
          && this.all_data.data.length > 0
        ) {
          this.invoice = this.all_data.data[0]

          // If doctor is not available at top level, try to get it from invoice_patient_surgery
          if (!this.invoice.doctor) {
            const invoicePatientSurgery = this.invoice.invoice_patient_surgery
              || this.invoice.invoicePatientSurgery

            if (invoicePatientSurgery) {
              // Check if patient_card_surgery is already included
              let patientCardSurgery = invoicePatientSurgery.patient_card_surgery
                || invoicePatientSurgery.patientCardSurgery

              // If not included, fetch it separately
              if (
                !patientCardSurgery
                && invoicePatientSurgery.patient_card_surgery_id
              ) {
                try {
                  const surgeryUrl = `/patient-surgery/${invoicePatientSurgery.patient_card_surgery_id}`
                  const response = await this.$http.get(surgeryUrl)
                  if (response.data && response.data.data) {
                    patientCardSurgery = response.data.data
                  } else if (response.data) {
                    patientCardSurgery = response.data
                  }
                } catch (error) {
                  console.error('Error fetching patient card surgery:', error)
                }
              }

              if (patientCardSurgery) {
                const doctorsPrices = patientCardSurgery.doctors_prices
                  || patientCardSurgery.doctorsPrices

                if (
                  doctorsPrices
                  && doctorsPrices.items
                  && Array.isArray(doctorsPrices.items)
                  && doctorsPrices.items.length > 0
                ) {
                  // Try to find surgeon first, otherwise use the first doctor
                  const surgeonItem = doctorsPrices.items.find(
                    item => item.doctor && item.type === 'surgeon',
                  )

                  const doctorItem = surgeonItem
                    || doctorsPrices.items.find(item => item.doctor)
                    || doctorsPrices.items[0]

                  if (doctorItem && doctorItem.doctor) {
                    this.invoice.doctor = doctorItem.doctor
                  }
                }
              }
            }
          }

          await this.$nextTick()

          setTimeout(() => {
            this.$refs.surgeryInsuranceInvoiceRef.click()
            this.changeStatusToPrintedInvoices()
          }, 100)
        }
      } catch (error) {
        console.error('Failed to print invoice:', error)
      } finally {
        this.loading = false
      }
    },

    changeStatusToPrintedInvoices() {
      console.log('changeStatusToPrintedInvoices')
      this.params.form = { status: 'printed' }
      this.params.path = `/invoices/${this.invoice.id}/changeStatus`
      this.$store
        .dispatch('put', this.params)
        .then(response => {
          this.getInvoices()
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: `${this.$t('common.success')}`,
              variant: 'success',
              text: `${response.data.message}`,
            },
          })
        })
        .catch(error => {
        })
    },

    clearSelection() {
      this.selectedInvoices = []
    },

    selectAll() {
      const invoicesData = getNestedProperty(this.invoices, 'data', [])
      this.selectedInvoices = [...invoicesData]
    },

    showBulkUpdateModal() {
      if (this.selectedInvoices.length === 0) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.select-invoices-first'),
          },
        })
        return
      }

      // Check if all selected invoices are approved or pending
      const hasInvalidStatus = this.selectedInvoices.some(invoice => {
        const status = getNestedProperty(invoice, 'status', '').toLowerCase()
        return status !== 'approved' && status !== 'pending'
      })

      if (hasInvalidStatus) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'warning',
            text: this.$t('common.only-approved-pending-invoices'),
          },
        })
        return
      }

      this.bulkUpdateForm.status = 'approved'
      this.$bvModal.show('modal-bulk-update-status')
    },

    async bulkUpdateStatus() {
      // Status check removed as we default to approved

      try {
        this.loading = true

        // Prepare the payload
        const payload = {
          invoices: this.selectedInvoices.map(invoice => ({
            id: invoice.id,
            status: 'approved',
          })),
        }

        console.log('Bulk update payload:', payload)

        // Call the bulk update API
        await this.$http.post('/invoices/bulkUpdateStatus', payload)

        this.$toast.success(this.$t('common.bulk-update-success'))

        // Clear selection and refresh data
        this.clearSelection()
        this.$bvModal.hide('modal-bulk-update-status')

        // Emit event to refresh parent data
        this.getInvoices()
      } catch (error) {
        console.error('Error bulk updating invoices:', error)
        this.$toast.error(this.$t('common.bulk-update-error'))
      } finally {
        this.loading = false
      }
    },

    onSelectionChange() {
      // Handle selection change if needed
    },

    // Modal methods for adding insurance invoice
    show(item, mode) {
      if (mode === 'create') {
        this.createMode = true
        this.EditForm = {
          patient_card_id: this.$route.params.patientCardId,
          amount: 0,
          _method: 'POST',
        }

        // Initialize surgery and doctor selections
        if (this.surgeryDataList && this.surgeryDataList.length > 0) {
          // Select first surgery
          this.surgerySelected = this.surgeryDataList[0]

          // Extract doctors from the first surgery
          const doctors = getNestedProperty(
            this.surgerySelected,
            'doctors',
            [],
          )
          this.doctorDataList = doctors

          // Select first doctor if available
          if (doctors.length > 0) {
            this.doctorSelected = doctors[0]
          } else {
            this.doctorSelected = null
          }

          console.log('Modal opened - Surgery selected:', this.surgerySelected)
          console.log('Modal opened - Doctor selected:', this.doctorSelected)
        } else {
          // If no surgeries available, clear selections
          this.surgerySelected = null
          this.doctorSelected = null
          this.doctorDataList = []
        }
      } else {
        this.createMode = false
        this.EditForm.id = item.id
        this.EditForm.patient_card_id = this.$route.params.patientCardId
        this.EditForm.service_id = this.service_id
        this.EditForm.amount = item.amount
        this.EditForm._method = 'PUT'
      }
      this.$bvModal.show('modal-out-invoice-edit')
    },

    async getSurgerys() {
      const { patientId } = this.$route.params
      if (!patientId) {
        this.surgeryDataList = []
        return
      }

      // Filter by patient_id: first get all patient cards for this patient, then filter surgeries by those card IDs
      try {
        // Fetch patient cards for this patient
        await this.getList(`/patientCard?filter[patient_id]=${patientId}&take=1000&sort=-id`)
        const patientCards = this.all_data && this.all_data.data ? this.all_data.data : []

        if (patientCards.length > 0) {
          // Extract card IDs and filter surgeries by them (comma-separated for multiple values)
          const cardIds = patientCards.map(card => card.id).join(',')
          const url = `patient-surgery?take=20&filter[patient_card_id]=${cardIds}&include=surgery,doctors&sort=-id`
          await this.getList(url)
        } else {
          // No patient cards found
          this.surgeryDataList = []
          return
        }
      } catch (error) {
        console.error('Error fetching patient cards for surgeries:', error)
        this.surgeryDataList = []
        return
      }
      this.surgeryDataList = this.all_data.data
      console.log('surgeryDataList', this.surgeryDataList)

      // Initialize with first surgery if available
      if (this.surgeryDataList && this.surgeryDataList.length > 0) {
        this.surgerySelected = this.surgeryDataList[0]

        // Extract doctors from the first surgery
        const doctors = getNestedProperty(this.surgerySelected, 'doctors', [])
        this.doctorDataList = doctors

        // Select first doctor if available
        if (doctors.length > 0) {
          this.doctorSelected = doctors[0]
        }

        console.log('Initialized with surgery:', this.surgerySelected)
        console.log('Initialized doctors:', this.doctorDataList)
      }
    },

    onSurgerySelectOpen() {
      console.log('Surgery select dropdown opened')
      this.getSurgerys()
    },

    onModalHide() {
      this.disabled = 'not_accepted'

      // Reset to first surgery if available
      if (this.surgeryDataList && this.surgeryDataList.length > 0) {
        this.surgerySelected = this.surgeryDataList[0]

        // Extract doctors from the first surgery
        const doctors = getNestedProperty(this.surgerySelected, 'doctors', [])
        this.doctorDataList = doctors

        // Select first doctor if available
        if (doctors.length > 0) {
          this.doctorSelected = doctors[0]
        }
      }
    },

    submitItem() {
      for (const item in this.errorForm) {
        this.errorForm[item] = ''
        if (this.EditForm[item] == '' || this.EditForm[item] == null) delete this.EditForm[item]
      }

      // Validate required fields
      if (!this.surgerySelected) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('surgery.please-select-surgery'),
          },
        })
        return
      }

      if (!this.doctorSelected) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('surgery.please-select-doctor'),
          },
        })
        return
      }

      if (this.createMode) {
        this.EditForm.patient_card_id = this.$route.params.patientCardId
        this.EditForm.surgery_id = getNestedProperty(
          this.surgerySelected,
          'surgery.id',
        )
        this.EditForm.doctor_id = getNestedProperty(this.doctorSelected, 'id')
        this.EditForm.type = 'invoice_insurance_surgery'

        this.invoicesData.push(this.EditForm)

        this.params.form = { invoices: this.invoicesData }
        this.params.path = '/invoices'
        this.$store
          .dispatch('create', this.params)
          .then(response => {
            this.$bvModal.hide('modal-out-invoice-edit')
            this.getInvoices()
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('common.success'),
                variant: 'success',
                text: this.$t('common.created-successfully'),
              },
            })
          })
          .catch(error => {
          })
      } else {
        this.params.form = this.EditForm
        this.params.path = `/invoices/${this.EditForm.id}`
        this.$store
          .dispatch('update', this.params)
          .then(response => {
            this.$bvModal.hide('modal-out-invoice-edit')
            this.getInvoices()
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('common.success'),
                variant: 'success',
                text: this.$t('common.updated-successfully'),
              },
            })
          })
          .catch(error => {
          })
      }
    },
    formatDate(surgeryDate) {
      const date = new Date(surgeryDate)
      const year = date.getFullYear()
      let month = `${date.getMonth() + 1}`
      let day = `${date.getDate()}`
      let hours = `${date.getHours()}`
      let minutes = `${date.getMinutes()}`
      let seconds = `${date.getSeconds()}`

      if (month.length < 2) month = `0${month}`
      if (day.length < 2) day = `0${day}`
      if (hours.length < 2) hours = `0${hours}`
      if (minutes.length < 2) minutes = `0${minutes}`
      if (seconds.length < 2) seconds = `0${seconds}`

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    formatDate(surgeryDate) {
      const date = new Date(surgeryDate)
      const year = date.getFullYear()
      let month = `${date.getMonth() + 1}`
      let day = `${date.getDate()}`
      let hours = `${date.getHours()}`
      let minutes = `${date.getMinutes()}`
      let seconds = `${date.getSeconds()}`

      if (month.length < 2) month = `0${month}`
      if (day.length < 2) day = `0${day}`
      if (hours.length < 2) hours = `0${hours}`
      if (minutes.length < 2) minutes = `0${minutes}`
      if (seconds.length < 2) seconds = `0${seconds}`

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    showSurgeryInvoice(item) {
      this.selectedSurgeryInvoice = item.invoice_patient_surgery
      this.$bvModal.show('modal-show-surgery-invoice')
    },

    openEditDetailsModal(item) {
      // Deep copy to avoid modifying the original object until update
      this.surgeryInvoiceEditForm = JSON.parse(JSON.stringify(item))
      this.$bvModal.show('modal-edit-surgery-invoice-details')
    },

    async updateEditedDetails() {
      try {
        this.loading = true
        const payload = { ...this.surgeryInvoiceEditForm }

        // Reformat date before sending
        payload.date = this.formatPmDate(payload.date)

        this.params.form = payload
        this.params.path = 'invoicePatientSergury'

        await this.$store.dispatch('create', this.params)

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('common.updated-successfully'),
          },
        })

        this.$bvModal.hide('modal-edit-surgery-invoice-details')
        this.$bvModal.hide('modal-show-surgery-invoice') // Close the first modal too
        this.getInvoices() // Refresh data reactively
      } catch (error) {
        console.error('Error updating surgery invoice details:', error)
        if (error.response && error.response.status === 400) {
          for (const item in error.response.data) {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'validation error',
                variant: 'danger',
                text: error.response.data[item][0],
              },
            })
          }
        } else {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Error',
              variant: 'danger',
              text: 'An error occurred while updating the surgery invoice details.',
            },
          })
        }
      } finally {
        this.loading = false
      }
    },

    // Format a date string from "2025-03-26 13:17:00 pm" to "2025-03-26 13:17:00"
    formatPmDate(dateString) {
      if (!dateString || typeof dateString !== 'string') {
        return ''
      }

      // Check if the date string contains 'am' or 'pm'
      if (
        dateString.toLowerCase().includes('am')
        || dateString.toLowerCase().includes('pm')
      ) {
        const parts = dateString.split(' ')
        if (parts.length >= 3) {
          const datePart = parts[0] // e.g., 2025-03-26
          const timePart = parts[1] // e.g., 13:17:00
          const ampm = parts[2].toLowerCase() // e.g., pm

          // Parse the time
          const [hours, minutes, seconds] = timePart.split(':').map(Number)

          // Convert to 24-hour format if needed
          let hour = hours
          if (ampm === 'pm' && hours < 12) {
            hour = hours + 12
          } else if (ampm === 'am' && hours === 12) {
            hour = 0
          }

          // Format the time with leading zeros
          const formattedHour = hour < 10 ? `0${hour}` : `${hour}`
          const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`
          const formattedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`

          // Return the formatted date and time
          return `${datePart} ${formattedHour}:${formattedMinutes}:${formattedSeconds}`
        }
      }

      // If the format is not as expected, return the original
      return dateString
    },
    async openAddSurgeryInvoiceModal(item) {
      this.selectedDepositInvoice = item
      console.log('selectedDepositInvoice', JSON.stringify(this.selectedDepositInvoice))
      try {
        await this.getList(
          `/patient-surgery?filter[patientCard.patient_id]=${this.selectedDepositInvoice.patient_card.patient_id}&filter[surgery_id]=${this.selectedDepositInvoice.surgery_id}`,
        )
        if (
          !this.all_data
          || !this.all_data.data
          || this.all_data.data.length === 0
        ) {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Error',
              variant: 'danger',
              text: 'Could not find the corresponding patient surgery record.',
              icon: 'AlertTriangleIcon',
            },
          })
          return
        }
        this.newSurgeryInvoicePatientCardSurgeryId = this.all_data.data[0].id
        this.$bvModal.show('modal-add-surgery-invoice')
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'Error',
            variant: 'danger',
            text: 'An error occurred while fetching surgery details.',
            icon: 'AlertTriangleIcon',
          },
        })
      }
    },
    async submitSurgeryInvoice() {
      try {
        const form = this.surgeryInvoiceForm
        const payload = {
          room_amount: form.room_amount || 0,
          admin_services_amount: form.admin_services_amount || 0,
          general_services_amount: form.general_services_amount || 0,
          nursing_amount: form.nursing_amount || 0,
          surgery_nurse_assist_amount: form.surgery_nurse_assist_amount || 0,
          surgery_room_amount: form.surgery_room_amount || 0,
          labour_room_amount: form.labour_room_amount || 0,
          medical_consumables_amount: form.medical_consumables_amount || 0,
          pre_incubator_amount: form.pre_incubator_amount || 0,
          pre_cpap_amount: form.pre_cpap_amount || 0,
          scanner_device_amount: form.scanner_device_amount || 0,
          surgeon_amount: form.surgeon_amount || 0,
          surgeon_assist_amount: form.surgeon_assist_amount || 0,
          anesthetist_amount: form.anesthetist_amount || 0,
          anaesthetist_assist_amount: form.anaesthetist_assist_amount || 0,
          pre_test_amount: form.pre_test_amount || 0,
          tissue_transplant_amount: form.tissue_transplant_amount || 0,
          op_nurse_amount: form.op_nurse_amount || 0,
          consultation_amount: form.consultation_amount || 0,
          pre_ventameter_amount: form.pre_ventameter_amount || 0,
          management_services_amount: form.management_services_amount || 0,
          delivery_room_amount: form.delivery_room_amount || 0,
          date: this.formatDate(form.date),
          related_invoice_id: this.selectedDepositInvoice.id,
          patient_card_surgery_id: this.newSurgeryInvoicePatientCardSurgeryId,
          patient_card_id: this.selectedDepositInvoice.patient_card_id,
        }

        this.params.form = payload
        this.params.path = '/invoicePatientSergury'

        await this.$store.dispatch('create', this.params)

        this.$bvModal.hide('modal-add-surgery-invoice')
        this.getInvoices()
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('common.created-successfully'),
            icon: 'CheckIcon',
          },
        })
      } catch (error) {
        if (error.response && error.response.status === 400) {
          for (const item in error.response.data) {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'validation error',
                variant: 'danger',
                text: error.response.data[item][0],
                icon: 'AlertTriangleIcon',
              },
            })
          }
        } else {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Error',
              variant: 'danger',
              text: 'An error occurred while creating the surgery invoice.',
              icon: 'AlertTriangleIcon',
            },
          })
        }
      }
    },
    async bulkPrintSummary() {
      if (this.selectedInvoices.length === 0 || !this.canBulkPrint) return

      const invoicesToPrint = this.selectedInvoices
      if (invoicesToPrint.length === 0) return

      this.bulkPrintInvoicesData = []
      this.isGeneratingPdf = true

      try {
        const invoiceIds = invoicesToPrint.map(inv => inv.id).join(',')
        const includeString = 'service,surgery,patientCard,doctor,invoiceItems,invoicePatientSurgery'

        const url = `/invoices?filter[id]=${invoiceIds}&include=${includeString}&take=1000`
        const response = await this.$http.get(url)

        if (response.data?.data && response.data.data.length > 0) {
          this.bulkPrintInvoicesData = response.data.data

          await this.$nextTick()
          await this.$nextTick()

          // Use the new shared PDF component
          setTimeout(() => {
            if (this.$refs.surgeryInvoicesListPdf) {
              this.$refs.surgeryInvoicesListPdf.generatePdf()
            }
          }, 100)

          // Safety timeout: ensure loading state clears even if preview is closed manually
          setTimeout(() => {
            if (this.isGeneratingPdf) {
              this.isGeneratingPdf = false
            }
          }, 15000)
        }
      } catch (error) {
        console.error('Error fetching invoices for bulk print:', error)
        this.isGeneratingPdf = false
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.error-occurred'),
          },
        })
      }
    },
    async bulkPrintDetailed() {
      if (this.selectedInvoices.length === 0 || !this.canBulkPrint) return

      const invoicesToPrint = this.selectedInvoices
      if (invoicesToPrint.length === 0) return

      this.bulkPrintInvoicesData = []

      try {
        const invoiceIds = invoicesToPrint.map(inv => inv.id).join(',')
        const includeString = 'service,surgery,patientCard,doctor,invoiceItems,invoicePatientSurgery'

        const url = `/invoices?filter[id]=${invoiceIds}&include=${includeString}&take=1000`
        const response = await this.$http.get(url)

        if (response.data?.data && response.data.data.length > 0) {
          this.bulkPrintInvoicesData = response.data.data

          await this.$nextTick()
          await this.$nextTick()

          setTimeout(() => {
            if (this.$refs.BulkPrintRef) {
              this.$refs.BulkPrintRef.click()
            } else {
              window.print()
            }

            setTimeout(() => {
              this.updateBulkPrintStatus()
            }, 1000)
          }, 500)
        }
      } catch (error) {
        console.error('Error fetching invoices for bulk print:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.error-occurred'),
          },
        })
      }
    },
    async updateBulkPrintStatus() {
      if (this.selectedInvoices.length === 0) return

      try {
        const invoicesPayload = this.selectedInvoices.map(invoice => ({
          id: invoice.id,
          status: 'printed',
        }))

        await this.$http.post('/invoices/bulkUpdateStatus', {
          invoices: invoicesPayload,
        })

        this.clearSelection()
        this.bulkPrintInvoicesData = []
        this.getInvoices()
      } catch (error) {
        console.error('Error updating bulk print status:', error)
      }
    },
  },
}
</script>

<style scoped>
.hidden {
  display: none !important;
}

.gap-2 {
  gap: 1rem;
}

/* Service Items Container Styling */
.test-items-container {
  min-width: 250px;
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 8px;
  border: 1px solid #e9ecef;
}

.test-item-row {
  padding: 6px 0;
}

.test-item-row.border-bottom {
  border-bottom: 1px solid #dee2e6 !important;
}

.test-item-name {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.test-item-amount {
  margin-left: 12px;
  white-space: nowrap;
}

/* Modal test items styling */
.test-item-modal-row {
  padding: 8px 0;
}

.test-item-modal-row.border-bottom {
  border-bottom: 1px solid #e9ecef !important;
}
/* Reuse your badge + collapse styling for consistency */
.accordion .badge-light,
.badge-light {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  transition: background-color 0.3s ease, box-shadow 0.3s ease,
    border-radius 0.3s ease;
  border-radius: 0.5rem;
}

.badge-light:hover {
  background-color: #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Expanded header connects with body */
.badge-light.badge-expanded {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom: none;
}

.collapse-content {
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-top: none;
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  padding: 1.5rem;
  margin-top: 0;
}

.collapse {
  transition: height 0.35s ease;
}

.collapse-icon {
  transition: transform 0.3s ease;
  color: #7367f0;
}
.collapse-icon.rotate-90 {
  transform: rotate(90deg);
}

.hidden {
  display: none !important;
}
.w-100 {
  width: 100% !important;
}

.pdf-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.pdf-loading-content {
  background: white;
  padding: 30px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

</style>

<style>
/* Print Summary Styles - Global */
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
